import React, { useEffect, useRef, useState } from "react";
import { cls } from "app/utils";
import { useTranslation } from "react-i18next";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import Button from "app/components/Button/ButtonVariant";
import CheckBox from "app/components/inputs/CheckBox";
import UserAvatar from "app/components/UserAvatar";
import upload from "app/utils/uploader";
import DateOfBirthField from "./DateOfBirthField";
import {
  FamilyMember,
  FamilyMemberFormData,
  FamilyMemberModalMode,
  FamilyMemberSubmitResult,
} from "../../types";
import styles from "./styles.module.scss";

interface Props {
  isOpen: boolean;
  mode: FamilyMemberModalMode;
  member?: FamilyMember | null;
  onClose: () => void;
  // Resolves { ok: true } on success (parent closes the modal); on failure
  // { ok: false, message? } keeps the modal open (entered data is not lost) and
  // surfaces the server message when there is one.
  onSubmit: (data: FamilyMemberFormData) => Promise<FamilyMemberSubmitResult>;
}

const EMPTY_FORM: FamilyMemberFormData = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  proxyConsent: false,
  avatarId: null,
};

const FamilyMemberModal = ({
  isOpen,
  mode,
  member,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FamilyMemberFormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isCreate = mode === "create";

  // Sync the form whenever the modal opens (prefill on edit, reset on create).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setServerError(null);
    setSubmitting(false);
    setUploading(false);
    if (member) {
      setForm({
        firstName: member.firstName,
        lastName: member.lastName,
        dateOfBirth: member.dateOfBirth,
        proxyConsent: false,
        avatarId: null,
      });
      setAvatarPreview(member.avatar);
    } else {
      setForm(EMPTY_FORM);
      setAvatarPreview(null);
    }
  }, [isOpen, member]);

  const update = (patch: Partial<FamilyMemberFormData>) => {
    setServerError(null);
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Reset the input so re-picking the same file fires onChange again.
    event.target.value = "";
    if (!file) {
      return;
    }
    setUploading(true);
    setServerError(null);
    try {
      const result = await upload(file, file.name, "avatar", "avatar");
      setAvatarPreview(result.url ?? null);
      update({ avatarId: result.id });
    } catch {
      setServerError(t("Something went wrong. Please try again."));
    } finally {
      setUploading(false);
    }
  };

  const requiredFilled =
    form.firstName.trim() !== "" &&
    form.lastName.trim() !== "" &&
    form.dateOfBirth !== "";
  const canSubmit =
    requiredFilled && (!isCreate || form.proxyConsent) && !uploading;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) {
      return;
    }
    setSubmitting(true);
    setServerError(null);
    const result = await onSubmit(form);
    if (result.ok) {
      // On success the parent closes the modal; no further state change needed.
      return;
    }
    setSubmitting(false);
    setServerError(
      result.message ?? t("Something went wrong. Please try again."),
    );
  };

  const avatarName = `${form.firstName} ${form.lastName}`.trim();

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.popup}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h4 className={styles.title}>
            {isCreate ? t("Add Family Member") : t("Edit Family Member")}
          </h4>
          <i className="dismiss ico ico-x" onClick={onClose} />
        </div>

        <div className={styles.content}>
          <div className={styles.photo_row}>
            <UserAvatar
              sizeWidth={64}
              sizeHeight={64}
              altName={avatarName || t("Profile Photo")}
              srcName={avatarPreview ?? "default"}
              userName={avatarName}
              className={cls("avatar", styles.photo_avatar)}
            />
            <div className={styles.photo_text}>
              <span className={styles.photo_title}>{t("Profile Photo")}</span>
              <span className={styles.photo_hint}>
                <button
                  type="button"
                  className={styles.upload_link}
                  onClick={handleUploadClick}
                  disabled={uploading}
                >
                  {uploading ? t("Uploading…") : t("Upload")}
                </button>{" "}
                {t("a photo or leave blank for initials")}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className={styles.file_input}
              onChange={handleFileChange}
            />
          </div>

          <div className={styles.name_row}>
            <span className={styles.field}>
              <label htmlFor="family_first_name">{t("First Name")}*</label>
              <input
                id="family_first_name"
                type="text"
                value={form.firstName}
                onChange={(e) => update({ firstName: e.target.value })}
              />
            </span>
            <span className={styles.field}>
              <label htmlFor="family_last_name">{t("Last Name")}*</label>
              <input
                id="family_last_name"
                type="text"
                value={form.lastName}
                onChange={(e) => update({ lastName: e.target.value })}
              />
            </span>
          </div>

          <span className={styles.field}>
            <label htmlFor="family_dob">{t("Date of Birth")}*</label>
            <DateOfBirthField
              id="family_dob"
              value={form.dateOfBirth}
              placeholder={t("Select date")}
              onChange={(iso) => update({ dateOfBirth: iso })}
            />
          </span>

          {isCreate && (
            <div className={styles.consent}>
              <CheckBox
                testId="family_proxy_consent"
                name="proxy_consent"
                checked={form.proxyConsent}
                onChange={(checked) => update({ proxyConsent: checked })}
                label={t(
                  "I confirm that I am the parent or legal guardian of this minor and I provide proxy consent for their participation and use of CoachNow.",
                )}
                squareCheckIcons
              />
            </div>
          )}

          {serverError && (
            <span className={styles.error}>{serverError}</span>
          )}
        </div>

        <div className={styles.footer}>
          <Button
            buttonType="secondary-outline"
            title={t("Cancel")}
            onClickFunc={onClose}
          />
          <Button
            buttonType="primary"
            title={isCreate ? t("Add") : t("Save")}
            disabled={!canSubmit}
            loading={submitting}
            onClickFunc={handleSubmit}
          />
        </div>
      </div>
    </ModalLayout>
  );
};

export default FamilyMemberModal;
